/**
 * @voltxampere/ui — HitTester (Sprint 12)
 *
 * Per Blueprint §12.3. Wraps the quadtree with z-order awareness:
 * a click at a single screen point may straddle a wire under a
 * component under a probe overlay; the renderer-visible top wins.
 *
 * Sprint 12 only inserts component + wire scene nodes — those are
 * the kinds with bounded geometry. The other variants (selection
 * box, ghost, probe overlay) are not selectable with the cursor
 * yet and are skipped at insert time.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { Quadtree } from "./quadtree.js";
const isHittable = (node) => node.kind === "component" || node.kind === "wire";
export class HitTester {
    tree;
    constructor(bounds) {
        this.tree = new Quadtree(bounds, { maxDepth: 6, maxItems: 8 });
    }
    /**
     * Wipe the index and reinsert every hittable node from the
     * fresh layer set. Called by the canvas effect each time the
     * scene snapshot changes.
     */
    rebuild(layers) {
        this.tree.clear();
        for (const layer of layers) {
            for (const node of layer.nodes) {
                if (!isHittable(node))
                    continue;
                this.tree.insert({
                    id: node.id,
                    bounds: node.bounds,
                    node,
                    zIndex: layer.zIndex,
                });
            }
        }
    }
    /**
     * Resolve a single point against the index, returning the
     * top-most (highest zIndex) hit. Ties break on insertion order
     * (first inserted wins) — wires sit at zIndex 2, components at
     * 3, so components naturally cover wires when a click lands on
     * the overlap.
     */
    hit(point, tolerance = 4) {
        const candidates = this.tree.queryPoint(point, tolerance);
        if (candidates.length === 0)
            return null;
        let best = null;
        for (const c of candidates) {
            if (best === null || c.zIndex > best.zIndex)
                best = c;
        }
        return best?.node ?? null;
    }
    /**
     * Resolve a rectangle against the index, returning every
     * intersecting hittable node. Sprint 12 doesn't trigger this
     * yet; Sprint 13 rubber-band selection will.
     */
    hitRect(rect) {
        return this.tree.queryRect(rect).map((it) => it.node);
    }
    get size() {
        return this.tree.size;
    }
}
//# sourceMappingURL=hit-test.js.map